import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const input = "w-full border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-sky-400";

function FixItem({ item, onDone }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.question);
  const saveFix = async () => {
    try {
      if (draft && item.question) {
        await api.put(`/admin/questions/${item.question.id}`, { stem: draft.stem, options: draft.options, correct_index: draft.correct_index });
      }
      await api.post(`/admin/fix-queue/${item.id}/resolve`, { resolution: "fixed" });
      toast.success("Resolved"); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dismiss = async () => {
    await api.post(`/admin/fix-queue/${item.id}/resolve`, { resolution: "dismissed" });
    onDone();
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`fix-item-${item.id}`}>
      <div className="flex flex-wrap gap-2 items-center text-xs font-mono text-zinc-500">
        <span className={`px-1.5 py-0.5 rounded border ${item.kind === "guarantee_claim" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>{item.kind}</span>
        <span>{item.cert_name}</span><span>· {item.email}</span><span>· {item.created_at?.slice(0, 10)}</span>
      </div>
      <p className="mt-2 text-sm"><b>Note:</b> {item.note}</p>
      {item.question && !editing && <p className="mt-2 text-sm text-zinc-600 border-l-2 border-zinc-200 pl-3">{item.question.stem}</p>}
      {editing && draft && (
        <div className="mt-3 space-y-2">
          <textarea className={input} rows={3} value={draft.stem} onChange={(e) => setDraft({ ...draft, stem: e.target.value })} data-testid="fix-edit-stem" />
          {draft.options.map((o, i) => (
            <div key={`${item.id}-${i}`} className="flex gap-2 items-center">
              <input type="radio" checked={draft.correct_index === i} onChange={() => setDraft({ ...draft, correct_index: i })} />
              <input className={input} value={o} onChange={(e) => setDraft({ ...draft, options: draft.options.map((x, j) => j === i ? e.target.value : x) })} />
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        {item.question && !editing && <button data-testid={`fix-edit-btn-${item.id}`} onClick={() => setEditing(true)} className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold hover:border-sky-400 transition-colors duration-150">Edit question inline</button>}
        <button data-testid={`fix-resolve-btn-${item.id}`} onClick={saveFix} className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors duration-150">{editing ? "Save fix & resolve" : "Resolve"}</button>
        <button data-testid={`fix-dismiss-btn-${item.id}`} onClick={dismiss} className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs text-zinc-500">Dismiss</button>
      </div>
    </div>
  );
}

export default function FixQueue() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/fix-queue").then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="fade-in max-w-3xl">
      <h1 className="text-2xl font-black mb-4">Fix Queue</h1>
      <p className="text-sm text-zinc-500 mb-4">Question reports and pass-guarantee claims land here with an inline editor.</p>
      <div className="space-y-3" data-testid="fix-queue-list">
        {items.map((i) => <FixItem key={i.id} item={i} onDone={load} />)}
        {items.length === 0 && <p className="text-zinc-400 text-sm">Queue is empty.</p>}
      </div>
    </div>
  );
}
